import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography
} from '@material-ui/core';
import DialogContentText from '@material-ui/core/DialogContentText';
import { Delete, DescriptionOutlined } from '@material-ui/icons';
import Alert from '@material-ui/lab/Alert';
import { useState } from 'react';
import LoadingOverlay from 'react-loading-overlay';
import { useSelector } from 'react-redux';

import { properCase } from '../../../shared/helpers/string-helpers';
import { EnumFileStatus } from '../../redux/scholarship.models';
import { selectError, selectIsLoading } from '../../redux/scholarship.selectors';

interface FileRow {
  docStatus?: EnumFileStatus;
}

interface FileCardProps {
  files: FileRow[];
  scanning: boolean;
  handleFileDelete: (confirmation: string) => void;
}

const styles = {
  largeIcon: {
    width: 35,
    height: 35,
    color: '#5e5e5e'
  },
  iconCol: {
    width: '30%',
    backgroundColor: '#fafafa',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  contentCol: {
    width: '50%',
    display: 'flex',
    alignItems: 'center'
  },
  actionCol: {
    width: '20%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }
};

const FileCard = ({ files, scanning, handleFileDelete }: FileCardProps): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const isDeleting = useSelector(selectIsLoading);
  const errorDeleting = useSelector(selectError);

  const handleToggle = (status: boolean) => {
    setOpen(status);
  };

  const handleConfirmType = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setConfirmation(e.target.value.toLowerCase().trim());
  };

  const handleAppDialogDelete = () => {
    if (confirmation === 'permanently delete') {
      handleToggle(false);
      handleFileDelete(confirmation);
    }
  };

  return (
    <>
      {errorDeleting && <Alert severity="error">{String(errorDeleting)}</Alert>}
      <LoadingOverlay active={isDeleting} spinner text="Deleting file.">
        {files.map((row: FileRow, index: number) => (
          <Card key={index} variant="outlined" style={{ marginTop: '10px' }}>
            <Grid container spacing={3} key={index}>
              <Grid item style={styles.iconCol}>
                <DescriptionOutlined style={styles.largeIcon} />
              </Grid>
              <Grid item style={styles.contentCol}>
                <CardContent style={{ marginTop: '10px' }}>
                  <Typography variant="h6" component="div">
                    Status
                  </Typography>
                  <Typography variant="body2">
                    {row.docStatus === EnumFileStatus.PENDING ? (
                      <>{scanning && <CircularProgress size={12} />} Scanning</>
                    ) : row.docStatus ? (
                      properCase(row.docStatus)
                    ) : (
                      ''
                    )}
                  </Typography>
                </CardContent>
              </Grid>
              <Grid item style={styles.actionCol}>
                <IconButton disabled={row.docStatus !== EnumFileStatus.RECEIVED} onClick={() => handleToggle(true)}>
                  <Delete />
                </IconButton>
              </Grid>
            </Grid>
          </Card>
        ))}
        <Dialog open={open} onClose={() => handleToggle(false)} aria-labelledby="form-dialog-app">
          <DialogTitle id="form-dialog-app">Are you sure?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              To delete this application, type in the word <b>permanently delete</b> then click Submit.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="confirm"
              label="type 'permanently delete' to confirm this action"
              type="text"
              fullWidth
              onChange={handleConfirmType}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handleToggle(false)} color="primary">
              Cancel
            </Button>
            <Button onClick={handleAppDialogDelete} color="primary">
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </LoadingOverlay>
    </>
  );
};

export default FileCard;
